/**
 * Static content for the WS4 Analysis modal.
 *
 * Ported from `_legacy/ws4-assetlocker.js` lines 296-532 (`#analysisModal`).
 * The vanilla source shipped real numbers and tables for every tab — this
 * module re-types that content as plain data so the React modal can
 * render it without any inline string concatenation.
 *
 * Accent tokens (`pillAccent`, `valueAccent`, `signalAccent`) map onto
 * the CSS-variable palette defined in `src/index.css` — see
 * `--accent-true-blue`, `--accent-sky`, `--accent-gold`, `--accent-green`,
 * `--accent-melon`, plus `--accent-danger` (the dedicated CSS var for
 * the legacy danger red).
 *
 * If a future workshop wires a different dataset, expose a per-workshop
 * lookup the way `ws4-data-tables/data-tables-content.ts` does. For now
 * WS4 is the only workshop with an analysis surface, so we export a
 * single record.
 */

export type AccentToken =
  | "true-blue"
  | "sky"
  | "gold"
  | "green"
  | "melon"
  | "purple"
  | "danger"
  | "muted";

export type Kpi = {
  label: string;
  value: string;
  valueSuffix?: string;
  sub: string;
  accent: AccentToken;
};

export type StatusPill = {
  text: string;
  accent: AccentToken;
};

export type OverviewRow = {
  dimension: string;
  detail: string;
  status: StatusPill;
};

export type CompletionBar = {
  label: string;
  pct: number;
  accent: AccentToken;
};

export type RolePill = {
  text: string;
  accent: AccentToken;
};

export type ParticipationRow = {
  cluster: RolePill;
  headcount: string;
  contributionScore: string;
  contributionAccent: AccentToken;
  outputs: string;
  engagement: string;
  engagementAccent: AccentToken;
  notes: string;
};

export type ActivityRow = {
  activity: string;
  groups: string;
  outputs: string;
  quality: string;
  qualityAccent: AccentToken;
  time: string;
};

export type SentimentRow = {
  statement: string;
  preScore: string;
  preAccent: AccentToken;
  postScore: string;
  postAccent: AccentToken;
  delta: string;
  deltaAccent: AccentToken;
  signal: StatusPill;
};

export type ConcernRow = {
  theme: string;
  frequency: string;
  severity: string;
  severityAccent: AccentToken;
  owner: string;
  status: StatusPill;
};

export type ModeDistribution = {
  mode: string;
  description: string;
  pct: number;
  accent: AccentToken;
};

export type ModePreferenceRow = {
  cluster: RolePill;
  inLoop: string;
  inLoopAccent?: AccentToken;
  aboveLoop: string;
  aboveLoopAccent?: AccentToken;
  onLoop: string;
  onLoopAccent?: AccentToken;
  outLoop: string;
  outLoopAccent?: AccentToken;
  reason: string;
};

export type ModeAssignmentRow = {
  domain: string;
  mode: StatusPill;
  rationale: string;
  reviewDate: string;
};

export type FindingRow = {
  id: string;
  finding: string;
  category: StatusPill;
  priority: string;
  priorityAccent: AccentToken;
  action: string;
};

export type NextStepRow = {
  action: string;
  owner: string;
  due: string;
  status: StatusPill;
};

/** AI-chat interaction telemetry for one workshop (ADO #689) — mirrors
 *  `analysis_generation.generate_chat_telemetry_section`'s shape. Only
 *  present when the BFF's `MC_CHAT_ANALYTICS` flag is on *and* at least one
 *  chat turn has been captured for the workshop; absent otherwise (never a
 *  fabricated empty section — see #689's privacy/telemetry design notes). */
export type ChatVolumeMetrics = {
  turnCount: number;
  participantCount: number;
  firstTimestamp: number | null;
  lastTimestamp: number | null;
};

export type ReferencedOutputRow = {
  outputId: string;
  label: string;
  count: number;
};

export type ParticipantBreakdownRow = {
  identity: string;
  turnCount: number;
  summary: string;
};

export type ChatTelemetry = {
  volumeMetrics: ChatVolumeMetrics;
  topics: string[];
  referencedOutputs: ReferencedOutputRow[];
  participantBreakdown: ParticipantBreakdownRow[];
};

export type AnalysisContent = {
  summary: {
    kpis: Kpi[];
    overview: OverviewRow[];
    completion: CompletionBar[];
  };
  participation: {
    kpis: Kpi[];
    rolesTable: ParticipationRow[];
    activitiesTable: ActivityRow[];
  };
  sentiment: {
    kpis: Kpi[];
    sentimentTable: SentimentRow[];
    concerns: ConcernRow[];
  };
  modes: {
    kpis: Kpi[];
    distribution: ModeDistribution[];
    preferencesTable: ModePreferenceRow[];
    assignmentsTable: ModeAssignmentRow[];
  };
  findings: {
    findingsTable: FindingRow[];
    nextSteps: NextStepRow[];
  };
  /** Optional — see `ChatTelemetry` doc comment. */
  chatTelemetry?: ChatTelemetry;
};

export const WS4_ANALYSIS: AnalysisContent = {
  summary: {
    kpis: [
      { label: "Participants", value: "24", sub: "Across 4 role clusters", accent: "sky" },
      { label: "Completion Rate", value: "92%", sub: "All activities attempted", accent: "green" },
      { label: "Avg Engagement", value: "4.1", valueSuffix: "/5", sub: "Self-reported score", accent: "true-blue" },
      { label: "AI Readiness Shift", value: "+28%", sub: "Pre → post session delta", accent: "green" },
      { label: "Open Action Items", value: "11", sub: "Assigned to owners", accent: "gold" },
    ],
    overview: [
      {
        dimension: "Session Title",
        detail: "Human-Agent Ways of Working",
        status: { text: "COMPLETE", accent: "green" },
      },
      {
        dimension: "Format",
        detail: "Facilitated workshop — 4 activities, 3.5 hrs",
        status: { text: "ON SCHEDULE", accent: "true-blue" },
      },
      {
        dimension: "Core Objective",
        detail:
          "Establish shared mental models for human-agent collaboration; agree operating mode norms",
        status: { text: "MET", accent: "green" },
      },
      {
        dimension: "Activities Covered",
        detail:
          "Role Mapping · Operating Mode Calibration · Stress Test Scenarios · Handoff Protocol Design",
        status: { text: "ALL 4", accent: "green" },
      },
      {
        dimension: "Facilitators",
        detail: "Lead: Jordan K. · AI Assist: XAES-AI · Observer: John C.",
        status: { text: "ACTIVE", accent: "sky" },
      },
      {
        dimension: "Primary Output",
        detail: "Draft Ways-of-Working charter + Mode Assignment Matrix",
        status: { text: "IN REVIEW", accent: "gold" },
      },
    ],
    completion: [
      { label: "Activity 1 — Role & Responsibility Mapping", pct: 100, accent: "green" },
      { label: "Activity 2 — Operating Mode Calibration", pct: 96, accent: "green" },
      { label: "Activity 3 — Stress Test Scenarios", pct: 88, accent: "gold" },
      { label: "Activity 4 — Handoff Protocol Design", pct: 83, accent: "gold" },
    ],
  },
  participation: {
    kpis: [
      { label: "Total Attendees", value: "24", sub: "vs 26 invited", accent: "sky" },
      { label: "Active Contributors", value: "21", sub: "87.5% of attendees", accent: "green" },
      { label: "Silent Observers", value: "3", sub: "No group output submitted", accent: "true-blue" },
      { label: "Avg Time On Task", value: "47m", sub: "Per activity block", accent: "gold" },
    ],
    rolesTable: [
      {
        cluster: { text: "DECISION MAKERS", accent: "true-blue" },
        headcount: "5",
        contributionScore: "4.6 / 5",
        contributionAccent: "green",
        outputs: "5 / 5",
        engagement: "High",
        engagementAccent: "green",
        notes: "Strong alignment on escalation thresholds",
      },
      {
        cluster: { text: "OPERATORS", accent: "sky" },
        headcount: "8",
        contributionScore: "4.2 / 5",
        contributionAccent: "green",
        outputs: "7 / 8",
        engagement: "High",
        engagementAccent: "green",
        notes: "Most granular feedback on handoff protocols",
      },
      {
        cluster: { text: "TECHNICAL LEADS", accent: "gold" },
        headcount: "6",
        contributionScore: "3.7 / 5",
        contributionAccent: "gold",
        outputs: "5 / 6",
        engagement: "Medium",
        engagementAccent: "gold",
        notes: "Raised API latency concerns in Activity 3",
      },
      {
        cluster: { text: "CHANGE & COMMS", accent: "purple" },
        headcount: "5",
        contributionScore: "3.5 / 5",
        contributionAccent: "gold",
        outputs: "4 / 5",
        engagement: "Medium",
        engagementAccent: "gold",
        notes: "Focused on org-change narrative framing",
      },
    ],
    activitiesTable: [
      {
        activity: "Role & Responsibility Mapping",
        groups: "4",
        outputs: "4 / 4",
        quality: "4.4 / 5",
        qualityAccent: "green",
        time: "52 min / 50 min",
      },
      {
        activity: "Operating Mode Calibration",
        groups: "4",
        outputs: "4 / 4",
        quality: "4.1 / 5",
        qualityAccent: "green",
        time: "48 min / 50 min",
      },
      {
        activity: "Stress Test Scenarios",
        groups: "3",
        outputs: "3 / 4",
        quality: "3.8 / 5",
        qualityAccent: "gold",
        time: "55 min / 50 min ⚠",
      },
      {
        activity: "Handoff Protocol Design",
        groups: "4",
        outputs: "3 / 4",
        quality: "3.6 / 5",
        qualityAccent: "gold",
        time: "44 min / 40 min",
      },
    ],
  },
  sentiment: {
    kpis: [
      { label: "Pre-Session Readiness", value: "2.9", valueSuffix: "/5", sub: "Avg self-rating", accent: "gold" },
      { label: "Post-Session Readiness", value: "3.9", valueSuffix: "/5", sub: "+34% uplift", accent: "green" },
      { label: "Trust in AI Agent", value: "3.6", valueSuffix: "/5", sub: "Post-session avg", accent: "true-blue" },
      { label: "Concern Score", value: "2.4", valueSuffix: "/5", sub: "Lower = better", accent: "gold" },
      { label: "NPS (Session)", value: "+62", sub: "Promoters: 71%", accent: "green" },
    ],
    sentimentTable: [
      {
        statement: "“I understand when to hand off a task to an AI agent”",
        preScore: "2.7 / 5",
        preAccent: "gold",
        postScore: "4.1 / 5",
        postAccent: "green",
        delta: "+51.9%",
        deltaAccent: "green",
        signal: { text: "STRONG LIFT", accent: "green" },
      },
      {
        statement: "“I know how to escalate an agent decision to a human”",
        preScore: "3.1 / 5",
        preAccent: "gold",
        postScore: "4.3 / 5",
        postAccent: "green",
        delta: "+38.7%",
        deltaAccent: "green",
        signal: { text: "STRONG LIFT", accent: "green" },
      },
      {
        statement: "“I’m comfortable working alongside an AI agent daily”",
        preScore: "2.9 / 5",
        preAccent: "gold",
        postScore: "3.5 / 5",
        postAccent: "gold",
        delta: "+20.7%",
        deltaAccent: "gold",
        signal: { text: "MODERATE LIFT", accent: "gold" },
      },
      {
        statement: "“I trust the agent to act within defined boundaries”",
        preScore: "2.5 / 5",
        preAccent: "gold",
        postScore: "3.6 / 5",
        postAccent: "true-blue",
        delta: "+44.0%",
        deltaAccent: "green",
        signal: { text: "STRONG LIFT", accent: "green" },
      },
      {
        statement: "“I feel my role is enhanced, not replaced, by AI”",
        preScore: "3.3 / 5",
        preAccent: "gold",
        postScore: "4.2 / 5",
        postAccent: "green",
        delta: "+27.3%",
        deltaAccent: "green",
        signal: { text: "STRONG LIFT", accent: "green" },
      },
      {
        statement: "“I understand the governance model for AI decisions”",
        preScore: "2.1 / 5",
        preAccent: "danger",
        postScore: "3.2 / 5",
        postAccent: "gold",
        delta: "+52.4%",
        deltaAccent: "gold",
        signal: { text: "WATCH — BELOW TARGET", accent: "gold" },
      },
    ],
    concerns: [
      {
        theme: "Accountability gap when agent makes an error",
        frequency: "8 mentions",
        severity: "High",
        severityAccent: "danger",
        owner: "Governance Lead",
        status: { text: "OPEN", accent: "gold" },
      },
      {
        theme: "Unclear escalation trigger criteria",
        frequency: "6 mentions",
        severity: "Medium",
        severityAccent: "gold",
        owner: "Ops Lead",
        status: { text: "IN PROGRESS", accent: "gold" },
      },
      {
        theme: "Agent confidence score not visible to operators",
        frequency: "5 mentions",
        severity: "Medium",
        severityAccent: "gold",
        owner: "Tech Lead",
        status: { text: "BACKLOGGED", accent: "true-blue" },
      },
      {
        theme: "Role boundary ambiguity in On-the-Loop mode",
        frequency: "4 mentions",
        severity: "Medium",
        severityAccent: "gold",
        owner: "Change Lead",
        status: { text: "OPEN", accent: "gold" },
      },
      {
        theme: "No fallback for agent downtime during live operations",
        frequency: "3 mentions",
        severity: "High",
        severityAccent: "danger",
        owner: "Ops Lead",
        status: { text: "IN PROGRESS", accent: "gold" },
      },
    ],
  },
  modes: {
    kpis: [
      { label: "Top Preferred Mode", value: "ON-THE-LOOP", sub: "38% of participants", accent: "true-blue" },
      { label: "Least Preferred Mode", value: "OUT-OF-LOOP", sub: "Only 8% selected", accent: "gold" },
      { label: "Mode Consensus Score", value: "71%", sub: "Intra-team agreement", accent: "sky" },
      { label: "Teams with Split Views", value: "2", sub: "Require follow-up", accent: "gold" },
    ],
    distribution: [
      {
        mode: "IN-THE-LOOP",
        description: "Human approves every action",
        pct: 25,
        accent: "gold",
      },
      {
        mode: "ABOVE-THE-LOOP",
        description: "Human sets goals, agent executes",
        pct: 29,
        accent: "sky",
      },
      {
        mode: "ON-THE-LOOP",
        description: "Human monitors, can intervene",
        pct: 38,
        accent: "true-blue",
      },
      {
        mode: "OUT-OF-LOOP",
        description: "Full agent autonomy",
        pct: 8,
        accent: "muted",
      },
    ],
    preferencesTable: [
      {
        cluster: { text: "DECISION MAKERS", accent: "true-blue" },
        inLoop: "20%",
        aboveLoop: "40%",
        aboveLoopAccent: "sky",
        onLoop: "35%",
        outLoop: "5%",
        reason: "Need strategic oversight without micro-managing",
      },
      {
        cluster: { text: "OPERATORS", accent: "sky" },
        inLoop: "25%",
        aboveLoop: "25%",
        onLoop: "45%",
        onLoopAccent: "true-blue",
        outLoop: "5%",
        reason: "Monitor & intervene on exceptions only",
      },
      {
        cluster: { text: "TECHNICAL LEADS", accent: "gold" },
        inLoop: "17%",
        aboveLoop: "33%",
        onLoop: "40%",
        onLoopAccent: "true-blue",
        outLoop: "10%",
        reason: "Comfortable with agent autonomy if guardrails are clear",
      },
      {
        cluster: { text: "CHANGE & COMMS", accent: "purple" },
        inLoop: "40%",
        inLoopAccent: "gold",
        aboveLoop: "30%",
        onLoop: "20%",
        outLoop: "10%",
        reason: "Managing org perception requires visible human control",
      },
    ],
    assignmentsTable: [
      {
        domain: "Customer query triage",
        mode: { text: "ON-THE-LOOP", accent: "true-blue" },
        rationale: "High volume, low risk — agent handles, human spot-checks",
        reviewDate: "Q3 2026",
      },
      {
        domain: "Compliance reporting",
        mode: { text: "IN-THE-LOOP", accent: "gold" },
        rationale: "Regulatory obligation requires human sign-off",
        reviewDate: "Ongoing",
      },
      {
        domain: "Campaign content generation",
        mode: { text: "ABOVE-THE-LOOP", accent: "sky" },
        rationale: "Human sets brief and brand guardrails; agent executes",
        reviewDate: "Q2 2026",
      },
      {
        domain: "Incident monitoring & alerting",
        mode: { text: "ON-THE-LOOP", accent: "true-blue" },
        rationale: "Agent monitors 24/7; human escalated within SLA",
        reviewDate: "Q3 2026",
      },
      {
        domain: "Routine data processing (ETL)",
        mode: { text: "OUT-OF-LOOP", accent: "green" },
        rationale: "Low risk, fully reversible, mature agent capability",
        reviewDate: "Annual",
      },
      {
        domain: "Executive briefing preparation",
        mode: { text: "IN-THE-LOOP", accent: "gold" },
        rationale: "High stakes content — human edits and approves",
        reviewDate: "Q4 2026",
      },
    ],
  },
  findings: {
    findingsTable: [
      {
        id: "F-01",
        finding:
          "Strong preference for On-The-Loop mode across operational roles — participants value visibility without bottlenecking agent throughput",
        category: { text: "MODE DESIGN", accent: "true-blue" },
        priority: "High",
        priorityAccent: "green",
        action: "Default new deployments to On-The-Loop; build monitoring dashboard",
      },
      {
        id: "F-02",
        finding:
          "Governance understanding remains the lowest-scored dimension post-session (3.2/5) — participants cannot reliably articulate who owns an agent decision",
        category: { text: "GOVERNANCE", accent: "gold" },
        priority: "Critical",
        priorityAccent: "danger",
        action: "Publish a 1-page decision ownership map; run a follow-up governance sprint",
      },
      {
        id: "F-03",
        finding:
          "Stress test scenarios (Activity 3) exceeded time budget by 10% — role-play scenarios triggered unresolved edge-case debates",
        category: { text: "FACILITATION", accent: "sky" },
        priority: "Medium",
        priorityAccent: "gold",
        action: "Pre-resolve top 3 edge cases before next stress test; tighten scenario constraints",
      },
      {
        id: "F-04",
        finding:
          "Change & Comms cluster shows higher demand for human control — driven by concern about narrative ownership, not technical risk",
        category: { text: "CHANGE MGMT", accent: "purple" },
        priority: "Medium",
        priorityAccent: "gold",
        action: "Co-create agent communication playbook with Comms team",
      },
      {
        id: "F-05",
        finding:
          "AI readiness uplift of +28% confirms workshop format is effective — scenario-based learning more impactful than didactic content",
        category: { text: "LEARNING", accent: "green" },
        priority: "Insight",
        priorityAccent: "green",
        action: "Scale scenario-based format to WS5 and WS6; capture scenario library",
      },
      {
        id: "F-06",
        finding:
          "Handoff protocol outputs were inconsistent across groups — 1 group produced a highly structured template while others remained conceptual",
        category: { text: "PROTOCOL", accent: "gold" },
        priority: "Medium",
        priorityAccent: "gold",
        action: "Standardise handoff template from Group 2 output; distribute for ratification",
      },
      {
        id: "F-07",
        finding:
          "Agent confidence score visibility was cited by 5 participants as a missing piece — operators want to see agent certainty before acting on output",
        category: { text: "UX / TOOLING", accent: "true-blue" },
        priority: "Medium",
        priorityAccent: "gold",
        action: "Add confidence indicator to operator UI; define confidence threshold thresholds",
      },
    ],
    nextSteps: [
      { action: "Publish Decision Ownership Map (1-pager)", owner: "Governance Lead", due: "2 weeks", status: { text: "OPEN", accent: "gold" } },
      { action: "Ratify Mode Assignment Matrix with execs", owner: "John C.", due: "1 week", status: { text: "IN PROGRESS", accent: "gold" } },
      { action: "Standardise Handoff Protocol Template", owner: "Ops Lead", due: "2 weeks", status: { text: "OPEN", accent: "gold" } },
      { action: "Add confidence score to operator UI (backlog)", owner: "Tech Lead", due: "Q3 sprint", status: { text: "BACKLOGGED", accent: "true-blue" } },
      { action: "Co-create Agent Comms Playbook", owner: "Change Lead", due: "3 weeks", status: { text: "OPEN", accent: "gold" } },
      { action: "Schedule WS5 governance sprint", owner: "Jordan K.", due: "+14 days", status: { text: "CONFIRMED", accent: "green" } },
      { action: "Capture and distribute scenario library", owner: "XAES-AI (AI)", due: "5 days", status: { text: "IN REVIEW", accent: "green" } },
    ],
  },
};

export function getAnalysis(workshopId: string): AnalysisContent | null {
  // Only WS4 has analysis content ported so far. Other workshops return
  // null and the modal will render its em-dash empty state.
  if (workshopId === "WS4") return WS4_ANALYSIS;
  return null;
}
